import { BALANCE } from "./balance.js";
import { towerStats, type Layout, type TowerKind } from "@empire/core-sim";
import { RESOURCE_KINDS, emptyResources, type Resources } from "./economy.js";

/**
 * Tower upgrades (D-119), from the Towers sheet's footnote:
 *
 *   "Tower upgrade cost = L1 cost x 1.5 (L2) and x 2.25 (L3). Tower level 2
 *    unlocks at Engineer's Guild 5, level 3 at Engineer's Guild 12."
 *
 * The footnote is prose, not a table the generator reads, so the four numbers
 * live here with this pointer back to it. Level-1 towers stay free for now
 * (D-119): charging for them would change every friend's saved defence mid-test.
 *
 * Upgrades are paid when the defence is SAVED, from the village's resources, as
 * the difference between what the new defence's upgrades are worth and what the
 * village has already paid for (a high-water mark per resource). So moving an
 * upgraded tower, or removing it and putting it back, costs nothing twice.
 */
export const TOWER_UPGRADE_MULTIPLIER_PERMILLE: Readonly<Record<number, number>> = {
  2: BALANCE.towerL2UpgradeMultiplier * 1000,
  3: BALANCE.towerL3UpgradeMultiplier * 1000,
};
export const TOWER_LEVEL_UNLOCK_GUILD: Readonly<Record<number, number>> = {
  1: 0,
  2: BALANCE.towerL2GuildUnlock,
  3: BALANCE.towerL3GuildUnlock,
};
export const MAX_TOWER_LEVEL = 3;

/** What it costs to raise one tower of this kind from `toLevel - 1` to `toLevel`. */
export function upgradeCost(kind: TowerKind, toLevel: number): Resources {
  const out = emptyResources();
  const permille = TOWER_UPGRADE_MULTIPLIER_PERMILLE[toLevel];
  if (!permille) return out;
  const base = towerStats(kind, 1).cost;
  out.wood = Math.round((base.wood * permille) / 1000);
  out.clay = Math.round((base.clay * permille) / 1000);
  out.iron = Math.round((base.iron * permille) / 1000);
  return out;
}

/** Everything spent on upgrades for the towers standing in this defence. */
export function upgradeValue(layout: Layout): Resources {
  const out = emptyResources();
  for (const tile of layout.tiles) {
    if (!tile.tower) continue;
    for (let level = 2; level <= tile.tower.level; level++) {
      const cost = upgradeCost(tile.tower.kind, level);
      for (const k of RESOURCE_KINDS) out[k] += cost[k];
    }
  }
  return out;
}

/** What saving this defence charges, given what the village has already paid. */
export function upgradeBill(layout: Layout, paid: Resources): Resources {
  const value = upgradeValue(layout);
  const out = emptyResources();
  for (const k of RESOURCE_KINDS) out[k] = Math.max(0, value[k] - paid[k]);
  return out;
}

/** The new high-water mark after a save. */
export function paidAfter(layout: Layout, paid: Resources): Resources {
  const value = upgradeValue(layout);
  const out = emptyResources();
  for (const k of RESOURCE_KINDS) out[k] = Math.max(value[k], paid[k]);
  return out;
}

/** Why a tower cannot be at this level with this Guild, or null. */
export function towerLevelBlock(level: number, engineersGuildLevel: number): string | null {
  if (level > MAX_TOWER_LEVEL) return `Towers go up to level ${MAX_TOWER_LEVEL}.`;
  const needs = TOWER_LEVEL_UNLOCK_GUILD[level] ?? Infinity;
  return engineersGuildLevel >= needs ? null : `Level ${level} needs Engineer's Guild ${needs}.`;
}

/** The first tower in this defence that its Guild does not allow, if any. */
export function lockedTower(layout: Layout): string | null {
  for (const tile of layout.tiles) {
    if (!tile.tower) continue;
    const reason = towerLevelBlock(tile.tower.level, layout.engineersGuildLevel);
    if (reason) return `${towerStats(tile.tower.kind, 1).name}: ${reason}`;
  }
  return null;
}
